import { Component, input, output, inject, computed, viewChild, TemplateRef } from '@angular/core';
import { CancelDeal, DealDetailLite, ProspectFormComponent, SalesApsService } from '@zayan-erp/sales';
import { DealStatus, NgbModalService, rxResourceFixed, SvgIconsComponent } from '@zayan-erp/shared';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AccordionModule } from '@openng/optimus-ui/accordion';
import { ButtonModule } from '@openng/optimus-ui/button';
import { ButtonGroup } from '@openng/optimus-ui/buttongroup';
import { RouterModule } from '@angular/router';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { catchError, map, of, Subject, tap } from 'rxjs';
import { DealPresenceChipComponent } from '../presence-chip/deal-presence-chip.component';

@Component({
  selector: 'app-deal-header',
  standalone: true,
  imports: [
    AccordionModule,
    ButtonModule,
    ButtonGroup,
    SvgIconsComponent,
    RouterModule,
    NgbTooltipModule,
    ProspectFormComponent,
    DealPresenceChipComponent
  ],
  templateUrl: './deal-header.component.html',
  styleUrls: ['./deal-header.component.scss']
})
export class DealHeaderComponent {
  #modalService = inject(NgbModal);
  #ngbModalService = inject(NgbModalService);
  editProspectModal = viewChild.required<TemplateRef<unknown>>('editProspectModal');
  openProspectModal = viewChild.required<TemplateRef<unknown>>('openProspectModal');
  onSubmitSubject = new Subject<void>();
  onSubmit$ = this.onSubmitSubject.asObservable();
  deal = input.required<DealDetailLite | null>();
  adjacentLots = input<{ previous?: { id: string; lotNo: string }; next?: { id: string; lotNo: string } }>({});
  _salesApsService = inject(SalesApsService);
  #dealIdByLot = new Map<string, string | null>();

  #previousDeal = rxResourceFixed({
    params: () => this.adjacentLots().previous?.id ?? null,
    stream: ({ params }) => (params ? this.#activeDealIdForLot(params) : of(null))
  });

  #nextDeal = rxResourceFixed({
    params: () => this.adjacentLots().next?.id ?? null,
    stream: ({ params }) => (params ? this.#activeDealIdForLot(params) : of(null))
  });

  previousLot = computed(() => this.#resolvedNeighbor(this.adjacentLots().previous, this.#previousDeal));
  nextLot = computed(() => this.#resolvedNeighbor(this.adjacentLots().next, this.#nextDeal));

  /** License image URL when available; null if no document, no front, or URL is empty. */
  prospectFrontDocumentUrl = computed(() => {
    const url = this.deal()?.prospect.uploadedCards?.[0]?.frontDocument?.directUrl;
    return url && typeof url === 'string' && url.trim() !== '' ? url : null;
  });

  licensePreviewModal = viewChild.required<TemplateRef<unknown>>('licensePreviewModal');

  onBackClick = output<void>();
  onNavigateLot = output<string>();
  onCancelDeal = output<CancelDeal>();
  handleBackClick() {
    this.onBackClick.emit();
  }

  goToLot(dealId: string | null | undefined): void {
    if (!dealId) return;
    this.onNavigateLot.emit(dealId);
  }

  #activeDealIdForLot(lotId: string) {
    if (this.#dealIdByLot.has(lotId)) {
      return of(this.#dealIdByLot.get(lotId) ?? null);
    }

    return this._salesApsService
      .getDealsLite(
        [
          DealStatus.Selection,
          DealStatus.ContractReady,
          DealStatus.PendingOffer,
          DealStatus.UnderContract,
          DealStatus.FinalSale
        ],
        undefined,
        undefined,
        false,
        lotId,
        true,
        5
      )
      .pipe(
        map(response => response.deals?.[0]?.dealId ?? null),
        tap(dealId => this.#dealIdByLot.set(lotId, dealId)),
        catchError(() => of(null))
      );
  }

  #resolvedNeighbor(
    lot: { id: string; lotNo: string } | undefined,
    resource: { status: () => string; value: () => string | null | undefined }
  ): { dealId: string; lotNo: string } | null {
    if (!lot || resource.status() !== 'resolved') return null;
    const dealId = resource.value();
    return dealId ? { dealId, lotNo: lot.lotNo } : null;
  }

  openLicensePreview(): void {
    this.#modalService.open(this.licensePreviewModal(), this.#ngbModalService.centerModal);
  }

  editProspect() {
    this.#modalService.open(this.editProspectModal(), { ...this.#ngbModalService.centerModal, size: 'xl' });
  }

  openProspectDialog() {
    this.#modalService.open(this.openProspectModal(), { ...this.#ngbModalService.centerModal, size: 'xl' });
  }

  closeModal() {
    this.#modalService.dismissAll();
    this._salesApsService.realoadDeal.next(null);
  }
}
